import {SocketClient} from './SocketClient'
import {ClientSocketMessageType, createBinaryClientSocketMessage} from '@colonist/socket'
import {ClientMessageSubType, ServerId, ServerRecipient, ServerType, SocketData} from '@colonist/types'
import * as MsgPack from '@msgpack/msgpack'
import {NetworkEnums} from '../../../../shared/GameEnums'
import * as Sentry from '@sentry/browser'

export class SocketSender {
    private readonly socket: SocketClient

    private sessionWasEstablished: boolean = false
    private readonly bufferedOutgoingSecondaryMessages: Uint8Array[] = []
    private bufferedOutgoingPrimaryMessage: Uint8Array | undefined

    constructor(socket: SocketClient, registerSessionEstablishedObserver: (callback: () => void) => void) {
        this.socket = socket

        registerSessionEstablishedObserver(() => this.onSessionEstablished())
    }

    sendToServer(subType: ClientMessageSubType, serverId: ServerId, data: Record<string, any>, isPrimaryAction: boolean = false) {
        if(!serverId) {
            try {
                const missingRecipientError = new Error(`Direct to server message has no recipient`) as any
                missingRecipientError.subType = subType
                missingRecipientError.data = data
                throw missingRecipientError
            } catch(error: any) {
                // eslint-disable-next-line no-console
                console.error(error)
                Sentry.captureException(error)
            }
            return // no point in sending it to the router
        }
        this.send(ClientSocketMessageType.RouteToServerDirect, subType, serverId, data, isPrimaryAction)
    }

    sendToServerType(subType: ClientMessageSubType, serverType: ServerType, data: Record<string, any>, isPrimaryAction: boolean = false) {
        this.send(ClientSocketMessageType.RouteToServerType, subType, serverType, data, isPrimaryAction)
    }

    sendNoop() {
        this.send(ClientSocketMessageType.SocketRouter, ClientMessageSubType.Noop, '', {}, false)
    }

    sendCloseSocket(closeCode: number) {
        const messagePayload = new Uint8Array([closeCode - 4000])
        const binarySocketMessage = createBinaryClientSocketMessage(ClientSocketMessageType.SocketRouter, ClientMessageSubType.CloseSocket, '', messagePayload)

        this.socket.send(binarySocketMessage)
    }

    sendPing(timestamp: number) {
        const returnMessage: SocketData = {
            id: `${NetworkEnums.State.SocketMonitorUpdate}`,
            data: {timestamp: timestamp},
        }
        this.send(ClientSocketMessageType.SocketRouter, ClientMessageSubType.Echo, '', returnMessage, false)
    }

    private onSessionEstablished() {
        this.sessionWasEstablished = true

        for(const bufferedOutgoingMessage of this.bufferedOutgoingSecondaryMessages) {
            this.socket.send(bufferedOutgoingMessage)
        }
        this.bufferedOutgoingSecondaryMessages.length = 0

        if(this.bufferedOutgoingPrimaryMessage) {
            this.socket.send(this.bufferedOutgoingPrimaryMessage)
            this.bufferedOutgoingPrimaryMessage = undefined
        }
    }

    private send(type: ClientSocketMessageType, subType: ClientMessageSubType, recipient: ServerRecipient, data: Record<string, any>, isPrimaryAction: boolean) {
        const messagePayload = MsgPack.encode(data)
        const binarySocketMessage = createBinaryClientSocketMessage(type, subType, recipient, messagePayload)

        if(!this.sessionWasEstablished) {
            if(isPrimaryAction) {
                this.bufferedOutgoingPrimaryMessage = binarySocketMessage
            } else {
                this.bufferedOutgoingSecondaryMessages.push(binarySocketMessage)
            }

            return
        }

        this.socket.send(binarySocketMessage)
    }
}
